import { apiRequest } from './client'
import { demoTelegramSubscriptions } from '../dev/buyerDemoData'
import { demoUiDelay, isDemoUiEnabled, withDemoUi } from '../dev/demoMode'
import { readInt, readString } from './json'

export type TelegramSubscriptionLinkDto = {
  botType: string
  purpose: string
  deepLink: string
  expiresAt: string | null
}

export type TelegramChatSubscriptionDto = {
  id: number
  botType: string
  purpose: string
  chatId: string
  organizationId: number | null
  active: boolean
  lastSeenAt: string | null
}

type Raw = Record<string, unknown>

const normalizeLink = (json: Raw): TelegramSubscriptionLinkDto => ({
  botType: readString(json, 'botType', 'bot_type') ?? '',
  purpose: (json.purpose as string) ?? '',
  deepLink: readString(json, 'deepLink', 'deep_link') ?? '',
  expiresAt: readString(json, 'expiresAt', 'expires_at'),
})

const normalizeSubscription = (json: Raw): TelegramChatSubscriptionDto => ({
  id: readInt(json, 'id', 'id'),
  botType: readString(json, 'botType', 'bot_type') ?? '',
  purpose: (json.purpose as string) ?? '',
  chatId: readString(json, 'chatId', 'chat_id') ?? '',
  organizationId:
    json.organizationId != null
      ? Number(json.organizationId)
      : json.organization_id != null
        ? Number(json.organization_id)
        : null,
  active: (json.active as boolean) ?? false,
  lastSeenAt: readString(json, 'lastSeenAt', 'last_seen_at'),
})

export const createTelegramSubscriptionLink = async (options: {
  botType: string
  purpose: string
  organizationId?: number
}): Promise<TelegramSubscriptionLinkDto> => {
  if (isDemoUiEnabled) {
    await demoUiDelay()
    return {
      botType: options.botType,
      purpose: options.purpose,
      deepLink: 'https://t.me/demo_provisium_bot?start=preview',
      expiresAt: null,
    }
  }
  const body: Record<string, unknown> = {
    bot_type: options.botType,
    purpose: options.purpose,
  }
  if (options.organizationId != null) {
    body.organization_id = options.organizationId
  }
  const result = await apiRequest<Raw>('/notifications/telegram/subscriptions/link', {
    method: 'POST',
    body,
  })
  return normalizeLink(result)
}

const asSubscriptionRows = (value: unknown): Raw[] => {
  if (Array.isArray(value)) {
    return value as Raw[]
  }
  if (value && typeof value === 'object') {
    const record = value as Raw
    if (Array.isArray(record.items)) {
      return record.items as Raw[]
    }
    if (Array.isArray(record.content)) {
      return record.content as Raw[]
    }
  }
  return []
}

export const listTelegramSubscriptions = async (): Promise<TelegramChatSubscriptionDto[]> =>
  withDemoUi(demoTelegramSubscriptions(), async () => {
  // Backend returns a JSON array; tolerate page wrappers / empty objects from proxies or mocks.
  const result = await apiRequest<unknown>('/notifications/telegram/subscriptions')
  return asSubscriptionRows(result).map(normalizeSubscription)
})
